/**
 * Bulk edit for catalog items — manufacturer, family, sale status, tags.
 *
 * Functional spec: `_BulkEditDialog` and `_bulk_edit_items` in
 * `qt_poc/catalog_qt.py`. **Only ticked fields are applied**, which is the
 * whole design: a dialog that always wrote every field would silently reset
 * the family of items the user only meant to re-tag.
 *
 * Applied one item at a time, as the desktop does. There is no bulk endpoint
 * and this deliberately does not invent one:
 *
 *   - `PUT /items/{id}` replaces the component list wholesale, so each item
 *     must be **read back first** and sent complete. A payload assembled from
 *     the grid row — which carries no components — would delete every
 *     component of every item it touched.
 *   - Saving item-by-item means a failure stops at that item and the ones
 *     already saved stay saved, which is what the desktop does and what the
 *     progress report below reflects. A partial result the user can see beats
 *     an all-or-nothing claim the API cannot actually honour.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Button, Checkbox, Group, Progress, Radio, Select, Stack, Text,
} from '@mantine/core'

import { Modal } from '../components/AppModal'
import { useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import type { Item, ItemDetail, MasterData } from '../api/types'

type TagMode = 'replace' | 'add' | 'remove'

const SALE_STATUSES = ['ACTIVE', 'EOS', 'EOL']

function tagList(tags: string): string[] {
  return tags.split(',').map((t) => t.trim()).filter(Boolean)
}

export function BulkEditModal({
  ids, master, opened, onClose,
}: {
  ids: number[]
  master?: MasterData
  opened: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [apply, setApply] = useState({
    manufacturer: false, family: false, sale_status: false, tags: false,
  })
  const [manufacturer, setManufacturer] = useState<string | null>(null)
  const [family, setFamily] = useState<string | null>(null)
  const [saleStatus, setSaleStatus] = useState<string | null>('ACTIVE')
  const [tags, setTags] = useState<string[]>([])
  const [tagMode, setTagMode] = useState<TagMode>('replace')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<number | null>(null)

  useEffect(() => {
    if (!opened) return
    setApply({ manufacturer: false, family: false, sale_status: false, tags: false })
    setManufacturer(null); setFamily(null); setSaleStatus('ACTIVE')
    setTags([]); setTagMode('replace'); setError(null); setDone(null)
  }, [opened])

  const anything = apply.manufacturer || apply.family || apply.sale_status || apply.tags

  async function run() {
    setError(null)
    if (apply.manufacturer && !manufacturer) return setError('בחר יצרן.')
    if (apply.family && !family) return setError('בחר משפחה.')
    if (apply.tags && tagMode !== 'replace' && !tags.length)
      return setError('בחר תגיות.')

    setDone(0)
    let saved = 0
    for (const id of ids) {
      try {
        // Read back first: the grid row has no components, and a PUT without
        // them deletes them all.
        const item = await api.get<ItemDetail>(`/items/${id}`)
        const next: Item = { ...item }
        if (apply.manufacturer) next.manufacturer = manufacturer!
        if (apply.family) next.family = family!
        if (apply.sale_status) next.sale_status = saleStatus!
        if (apply.tags) {
          const existing = tagList(next.tags || '')
          if (tagMode === 'add') {
            next.tags = [...new Set([...existing, ...tags])].sort().join(',')
          } else if (tagMode === 'remove') {
            next.tags = existing.filter((t) => !tags.includes(t)).join(',')
          } else {
            next.tags = tags.join(',')
          }
        }
        await api.put(`/items/${id}`, next)
        saved += 1
        setDone(saved)
      } catch (err) {
        // Stop where it broke and say so. The items already written stay
        // written — pretending otherwise would be worse than the truth.
        setError(
          `${err instanceof ApiError ? err.message : 'השמירה נכשלה'} — ` +
          `${saved} מתוך ${ids.length} פריטים עודכנו לפני הכשל.`)
        void qc.invalidateQueries({ queryKey: ['items'] })
        setDone(null)
        return
      }
    }
    void qc.invalidateQueries({ queryKey: ['items'] })
    void qc.invalidateQueries({ queryKey: ['master-data'] })
    notifications.show({ message: `עודכנו ${saved} פריטים`, color: 'green' })
    onClose()
  }

  const running = done !== null

  return (
    <Modal opened={opened} onClose={onClose} title={`עריכה מרובה — ${ids.length} פריטים`}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Text size="sm" c="dimmed">סמן את השדות שברצונך לשנות. שדה שאינו מסומן לא ייגע.</Text>

        <Group align="flex-end" wrap="nowrap">
          <Checkbox
            label="יצרן" checked={apply.manufacturer}
            onChange={(e) => {
              const checked = e.currentTarget.checked
              setApply((a) => ({ ...a, manufacturer: checked }))
            }}
            style={{ width: 90 }}
          />
          <Select
            searchable disabled={!apply.manufacturer} style={{ flex: 1 }}
            data={master?.manufacturers ?? []} value={manufacturer}
            onChange={setManufacturer} placeholder="בחר"
          />
        </Group>

        <Group align="flex-end" wrap="nowrap">
          <Checkbox
            label="משפחה" checked={apply.family}
            onChange={(e) => {
              const checked = e.currentTarget.checked
              setApply((a) => ({ ...a, family: checked }))
            }}
            style={{ width: 90 }}
          />
          <Select
            searchable disabled={!apply.family} style={{ flex: 1 }}
            data={master?.families ?? []} value={family}
            onChange={setFamily} placeholder="בחר"
          />
        </Group>

        <Group align="flex-end" wrap="nowrap">
          <Checkbox
            label="סטטוס" checked={apply.sale_status}
            onChange={(e) => {
              const checked = e.currentTarget.checked
              setApply((a) => ({ ...a, sale_status: checked }))
            }}
            style={{ width: 90 }}
          />
          <Select
            disabled={!apply.sale_status} style={{ flex: 1 }}
            data={SALE_STATUSES} value={saleStatus} onChange={setSaleStatus}
          />
        </Group>

        <Group align="flex-end" wrap="nowrap">
          <Checkbox
            label="תגיות" checked={apply.tags}
            onChange={(e) => {
              const checked = e.currentTarget.checked
              setApply((a) => ({ ...a, tags: checked }))
            }}
            style={{ width: 90 }}
          />
          <Radio.Group
            value={tagMode}
            onChange={(v) => setTagMode(v as TagMode)}
            style={{ flex: 1 }}
          >
            <Group gap="md">
              <Radio value="replace" label="החלף" disabled={!apply.tags} />
              <Radio value="add" label="הוסף" disabled={!apply.tags} />
              <Radio value="remove" label="הסר" disabled={!apply.tags} />
            </Group>
          </Radio.Group>
        </Group>
        {apply.tags && (
          <>
            <Checkbox.Group value={tags} onChange={setTags}>
              <Group gap="sm">
                {(master?.tags ?? []).map((tag) => (
                  <Checkbox key={tag} value={tag} label={tag} />
                ))}
              </Group>
            </Checkbox.Group>
            {tagMode === 'replace' && (
              <Text size="xs" c="dimmed">
                "החלף" עם אפס תגיות מסומנות מרוקן את התגיות של כל הפריטים.
              </Text>
            )}
          </>
        )}

        {running && (
          <Stack gap={4}>
            <Progress value={(done / ids.length) * 100} />
            <Text size="xs" c="dimmed">{done} / {ids.length}</Text>
          </Stack>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={running}>ביטול</Button>
          <Button onClick={() => void run()} loading={running} disabled={!anything}>
            החל על {ids.length} פריטים
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
